import { ReportType } from '../../enums/ReportType';

/**
 * Reports V2 category keys. Each maps to an i18n label under 'reportingV2.categories.<key>'.
 * Ordering of this array defines the menu display order.
 */
export const ReportCategory = {
  Inventory: 'inventory',
  CostAndMargin: 'costAndMargin',
  Sales: 'sales',
  PurchasingAndDeliveries: 'purchasingAndDeliveries',
  MovementsAndActivity: 'movementsAndActivity',
  Administration: 'administration',
} as const;

export type ReportCategoryKey = (typeof ReportCategory)[keyof typeof ReportCategory];

export const ReportCategoryOrder: ReportCategoryKey[] = [
  ReportCategory.Inventory,
  ReportCategory.CostAndMargin,
  ReportCategory.Sales,
  ReportCategory.PurchasingAndDeliveries,
  ReportCategory.MovementsAndActivity,
  ReportCategory.Administration,
];

/**
 * Maps each report type to its V2 category. Reports without an entry fall back to
 * <see cref="ReportCategory.Inventory"/>-agnostic grouping handled by the menu (they are placed
 * under 'Administration' is NOT assumed — unmapped reports are hidden until categorised).
 */
export const ReportTypeCategoryMap: Partial<Record<ReportType, ReportCategoryKey>> = {
  [ReportType.StockOnHand]: ReportCategory.Inventory,
  [ReportType.ExtendedStockCount]: ReportCategory.Inventory,
  [ReportType.StockCountSummary]: ReportCategory.Inventory,
  [ReportType.StockDays]: ReportCategory.Inventory,
  [ReportType.inventoryExtension]: ReportCategory.Inventory,
  [ReportType.ItemStockOut]: ReportCategory.Inventory,
  [ReportType.ConstrainedItem]: ReportCategory.Inventory,
  [ReportType.MadeInStore]: ReportCategory.Inventory,
  [ReportType.ActualVsTheory]: ReportCategory.Inventory,

  [ReportType.CostOfSales]: ReportCategory.CostAndMargin,
  [ReportType.Cogs]: ReportCategory.CostAndMargin,
  [ReportType.Margin]: ReportCategory.CostAndMargin,
  [ReportType.StockCountVsUsage]: ReportCategory.CostAndMargin,

  [ReportType.SalesCheck]: ReportCategory.Sales,
  [ReportType.Invoice]: ReportCategory.Sales,
  [ReportType.giveawaysSamples]: ReportCategory.Sales,

  [ReportType.Receipt]: ReportCategory.PurchasingAndDeliveries,
  [ReportType.CreditPurchase]: ReportCategory.PurchasingAndDeliveries,
  [ReportType.Delivery]: ReportCategory.PurchasingAndDeliveries,
  [ReportType.OutstandingDeliveries]: ReportCategory.PurchasingAndDeliveries,
  [ReportType.DcShortage]: ReportCategory.PurchasingAndDeliveries,
  [ReportType.ForecastOrders]: ReportCategory.PurchasingAndDeliveries,

  [ReportType.Transfers]: ReportCategory.MovementsAndActivity,
  [ReportType.Waste]: ReportCategory.MovementsAndActivity,
  [ReportType.ItemActivity]: ReportCategory.MovementsAndActivity,
  [ReportType.ItemUsage]: ReportCategory.MovementsAndActivity,
  [ReportType.ItemTimeout]: ReportCategory.MovementsAndActivity,

  [ReportType.UserReport]: ReportCategory.Administration,
};
